import type { ReactNode } from 'react';

import { cn } from '@core/lib/cn';

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

export interface BadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
  /**
   * Prefix read out before the badge text, e.g. "Order status:". Order status
   * badges are meaningless out of context to a screen-reader user otherwise.
   */
  srPrefix?: string;
}

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-sunken text-ink-muted border-line',
  brand: 'bg-brand-soft text-brand-ink border-brand-ink/30',
  success: 'bg-success-bg text-success border-success-border',
  warning: 'bg-warning-bg text-warning border-warning-border',
  danger: 'bg-danger-bg text-danger border-danger-border',
};

/**
 * Status pill. Text-first: the tone is decoration, the word carries the meaning,
 * so "Printing" reads correctly in greyscale and in a screen reader.
 */
export function Badge({ tone = 'neutral', children, className, srPrefix }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        TONES[tone],
        className,
      )}
    >
      {srPrefix ? <span className="sr-only">{srPrefix} </span> : null}
      {children}
    </span>
  );
}
